<script setup lang="ts">
// ===================================================================
// Imports
// ===================================================================
import { onBeforeMount, computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useIrveStore } from "@modules/irve/stores/irve.store"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { formatDate } from "@core/helpers"
import { groupSimilarBornes } from "@modules/irve/helpers"

// ===================================================================
// Services et variables
// ===================================================================
const confirm = useConfirm()
const irveStore = useIrveStore()
const selectedProduct = ref()
defineEmits(["click:create"])

const downloadAnalysis = irveStore.getDownloadReportFetch()

/**
 * Récupération des analyses du bâtiment
 */
onBeforeMount(async () => {
    await irveStore.fetchAnalyses.fetch()
    if (!irveStore.getCurrentAnalysis) {
        irveStore.fetchCurrentAnalysis.fetch()
    }
})

const getAnalysesListFormated = computed(() => {
    return irveStore.analyses?.map((analysis) => {
        return {
            _id_analysis: analysis._id_analysis,
            name: analysis.name || "-",
            createdAt: analysis.created_at,
            bornes: groupSimilarBornes(analysis.inputs.bornes).map(
                (borne) =>
                    `${borne.quantity} Borne${borne.quantity > 1 ? "s" : ""} ${borne.power} kVA`
            ),
            cmdps: analysis?.outputs?.periode?.cout_elec_turpeCMDPS?.sum,
            status: analysis.status,
        }
    })
})

// ===================================================================
// Bouton d'actions
// ===================================================================
/**
 * Liste des actions des boutons
 */
const getButtonActions = computed(() => {
    return [
        {
            label: t("view"),
            action: "view",
            icon: "pi pi-eye",
            severity: "contrast",
        },
        {
            label: t("remove"),
            action: "remove",
            icon: "pi pi-trash",
            severity: "contrast",
        },
    ]
})

/**
 * Gestion des actions des boutons
 */
function onClickAction(type: string, analysisId: string) {
    switch (type) {
        case "view":
            onViewAnalysis(analysisId)
            break
        case "download":
            onDownloadAnalysis(analysisId)
            break
        case "remove":
            onRemoveAnalysis(analysisId)
            break
    }
}

/**
 * Quand l'utilisateur clique sur le bouton de téléchargement
 * Télécharge l'analyse sélectionnée à partir du store
 */
function onDownloadAnalysis(analysisId: string) {
    downloadAnalysis.fetch(analysisId)
}

/**
 * Quand l'utilisateur clique sur le bouton de visualisation.
 * Met à jour le store pour afficher l'analyse sélectionnée
 */
function onViewAnalysis(analysisId: string) {
    irveStore.viewAnalysis(analysisId)
    // scroll jusqu'au graphique
    setTimeout(() => {
        document.getElementById("irve-view")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "center",
        })
    }, 200)
}

const toast = useToast()
/**
 * Prend une analyse comme paramètre et la supprime de la liste
 * après confirmation de la suppression
 * @param data Analyse à supprimer
 */
function onRemoveAnalysis(analysisId: string) {
    confirm.require({
        header: "Confirmation",
        message: t("delete.message"),
        icon: "pi pi-exclamation-triangle",
        rejectLabel: t("delete.cancel"),
        acceptProps: {
            icon: "pi pi-trash",
            label: t("delete.accept"),
            severity: "danger",
        },
        rejectProps: {
            label: t("delete.cancel"),
            severity: "secondary",
            outlined: true,
        },
        accept: async () => {
            const deleteAnalysis = irveStore.getDeleteAnalysisFetch()
            await deleteAnalysis.fetch({ _id_analysis: analysisId })
            // affiche une notification si une erreur est survenue
            if (deleteAnalysis.hasError.value) {
                toast.add({
                    severity: "error",
                    summary: "Analyse non supprimée",
                    detail: "Une erreur est survenue lors de la suppression de l'analyse. Veuillez réessayer plus tard.",
                })
            }
        },
        reject: () => {},
    })
}

function getTagSeverity(cmdps: number) {
    if (cmdps === 0 || cmdps === undefined) {
        return "info"
    }
    return "danger"
}

function formatCmdps(cmdps: number) {
    if (cmdps === undefined) {
        return "-"
    }
    return Intl.NumberFormat("fr", {
        style: "currency",
        currency: "EUR",
    }).format(cmdps)
}

// ===================================================================
// Langue
// ===================================================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Historique des analyses",
            header: {
                realisation_date: "Date de réalisation",
                name: "Nom",
                bornes: "Bornes",
                cmdps: "Coût de dépassement (€)",
            },
            delete: {
                message: "Voulez vous supprimer l’analyse ?",
                cancel: "Non, annuler",
                accept: "Oui, supprimer",
            },
            view: "Voir le rapport",
            download: "Télécharger le rapport",
            remove: "Supprimer",
            empty_list: "Aucune analyse trouvée sur ce bâtiment. ",
            create_analyse: "Créer une analyse",
            loading:
                "Récupération des analyses en cours, veuillez patienter...",
            error_fetch:
                "Une erreur est survenue lors de la récupération des analyses.",
            retry: "Réessayer",
            failure: "Analyse échouée",
        },
    },
})
</script>

<template>
    <BaseCard class="irve-history">
        <template #header>
            <!-- Titre -->
            <h1
                class="p-6 gap-2.5 border-b rounded-t-lg border-gray-200 text-lg font-bold text-slate-700 bg-slate-50"
            >
                {{ t("title") }}
            </h1>
        </template>
        <template #content>
            <div class="p-2">
                <!-- Table -->
                <BaseDataTable
                    v-if="
                        (!irveStore.fetchAnalyses.isFetching &&
                            irveStore.analyses?.length) ||
                        irveStore.fetchAnalyses.isFetching
                    "
                    v-model:selection="selectedProduct"
                    :loading="irveStore.fetchAnalyses.isFetching"
                    removable-sort
                    :value="getAnalysesListFormated"
                    paginator
                    :rows="5"
                    resizable-columns
                    column-resize-mode="fit"
                    :rows-per-page-options="[5, 10, 20, 50]"
                    table-style="min-width: 10rem"
                    sort-field="createdAt"
                    :sort-order="-1"
                >
                    <BaseColumn
                        field="name"
                        :header="t('header.name')"
                        sortable
                        style="width: 20%"
                    ></BaseColumn>
                    <BaseColumn
                        field="createdAt"
                        :header="t('header.realisation_date')"
                        sortable
                        style="width: 10%"
                    >
                        <template #body="slotProps">
                            {{
                                formatDate(
                                    slotProps.data.createdAt,
                                    "DD/MM/YYYY [à] HH:mm"
                                )
                            }}
                        </template>
                    </BaseColumn>
                    <BaseColumn
                        field="bornes"
                        :header="t('header.bornes')"
                        style="width: 30%"
                    >
                        <template #body="slotProps">
                            <BaseChip
                                v-for="(borne, index) in slotProps.data.bornes"
                                :key="index"
                                :label="borne"
                                class="mr-2"
                            />
                        </template>
                    </BaseColumn>
                    <BaseColumn
                        field="cmdps"
                        :header="t('header.cmdps')"
                        sortable
                    >
                        <template #body="slotProps">
                            <BaseTag
                                v-if="slotProps.data.cmdps !== undefined"
                                :severity="getTagSeverity(slotProps.data.cmdps)"
                            >
                                {{ formatCmdps(slotProps.data.cmdps) }}
                            </BaseTag>
                            <span
                                v-else-if="slotProps.data.status === 'FAILURE'"
                            >
                                <BaseTag severity="warn">
                                    {{ t("failure") }}
                                </BaseTag>
                            </span>
                        </template>
                    </BaseColumn>
                    <BaseColumn field="buttons">
                        <template #body="{ data: analysis }">
                            <div class="flex flex-wrap gap-1">
                                <BaseButton
                                    v-for="button in getButtonActions"
                                    :key="button.action"
                                    :icon="button.icon"
                                    :severity="button.severity"
                                    rounded
                                    variant="link"
                                    v-tooltip.left="{
                                        value: t(button.action),
                                        showDelay: 2000,
                                    }"
                                    @click="
                                        onClickAction(
                                            button.action,
                                            analysis._id_analysis
                                        )
                                    "
                                />
                            </div>
                        </template>
                    </BaseColumn>
                    <template #loading>
                        <p>{{ t("loading") }}</p>
                    </template>
                </BaseDataTable>
                <BaseMessage
                    severity="info"
                    v-if="
                        !irveStore.fetchAnalyses.isFetching &&
                        !irveStore.analyses?.length &&
                        !irveStore.fetchAnalyses.hasError
                    "
                >
                    <p>{{ t("empty_list") }}</p>
                    <BaseButton
                        class="mt-4"
                        :label="t('create_analyse')"
                        @click="$emit('click:create')"
                    ></BaseButton>
                </BaseMessage>
                <BaseMessage
                    severity="error"
                    v-if="irveStore.fetchAnalyses.hasError"
                >
                    <p>{{ t("error_fetch") }}</p>
                    <BaseButton
                        class="mt-4"
                        severity="danger"
                        :label="t('retry')"
                        @click="irveStore.fetchAnalyses.fetch()"
                    ></BaseButton>
                </BaseMessage>
            </div>
            <BaseConfirmDialog class="max-w-[30rem] w-full"></BaseConfirmDialog>
            <BaseToast />
        </template>
    </BaseCard>
</template>
